(() => {
  'use strict';

  const root = document.querySelector('[data-zo-ai-tryon-root]');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const apiBase = root.dataset.apiBase || '/apps/tryon-v2';
  const maxBytes = Number(root.dataset.maxBytes || 10485760);
  const marketCountryCandidate = String(
    root.dataset.marketCountryCode || window.Shopify?.country || '',
  ).trim().toUpperCase();
  const marketCountryCode = /^[A-Z]{2}$/.test(marketCountryCandidate)
    ? marketCountryCandidate
    : null;
  const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const collectionActivities = Object.freeze({
    '/collections/running': 'running',
    '/collections/hiking-sunglasses': 'hiking',
    '/collections/watersport': 'water-sports',
    '/collections/fishing': 'fishing',
    '/collections/cycling-sunglasses': 'cycling',
    '/collections/driving': 'driving',
    '/collections/golf-sunglasses': 'golf',
    '/collections/everyday': 'everyday',
    '/collections/high-mountain-trekking-sunglasses': 'alpine',
  });

  const panel = root.querySelector('[data-zo-ai-tryon-panel]');
  const scenes = [...root.querySelectorAll('[data-zo-ai-tryon-scene]')];
  const form = root.querySelector('[data-zo-ai-tryon-scene="input"]');
  const workflowSteps = [...root.querySelectorAll('[data-zo-ai-workflow-step]')];
  const activityButtons = [...root.querySelectorAll('[data-zo-ai-activity]')];
  const randomActivityButton = root.querySelector('[data-zo-ai-activity-random]');
  const workflowBack = root.querySelector('[data-zo-ai-workflow-back]');
  const uploadInput = root.querySelector('[data-zo-ai-tryon-upload]');
  const uploadLabel = root.querySelector('[data-zo-ai-upload-label]');
  const defaultUploadLabel = uploadLabel.textContent;
  const photoTabs = [...root.querySelectorAll('[data-zo-ai-photo-tab]')];
  const photoPanes = [...root.querySelectorAll('[data-zo-ai-photo-pane]')];
  const mediaFrame = root.querySelector('[data-zo-ai-media-frame]');
  const previewWrap = root.querySelector('[data-zo-ai-preview-wrap]');
  const preview = root.querySelector('[data-zo-ai-tryon-preview]');
  const retakeButton = root.querySelector('[data-zo-ai-retake]');
  const generateButton = root.querySelector('[data-zo-ai-tryon-generate]');
  const inputStatus = root.querySelector('[data-zo-ai-tryon-status]');
  const generatingImage = root.querySelector('[data-zo-ai-generating-image]');
  const resultImage = root.querySelector('[data-zo-ai-tryon-result-image]');
  const resultStatus = root.querySelector('[data-zo-ai-result-status]');
  const retryButton = root.querySelector('[data-zo-ai-tryon-retry]');
  const downloadButton = root.querySelector('[data-zo-ai-tryon-download]');
  const shareButtons = [...root.querySelectorAll('[data-zo-ai-share]')];
  const closeButtons = [...root.querySelectorAll('[data-zo-ai-tryon-close]')];
  const cameraPane = root.querySelector('[data-zo-ai-photo-pane="camera"]');
  const cameraVideo = root.querySelector('[data-zo-ai-tryon-video]');
  const cameraCapture = root.querySelector('[data-zo-ai-camera-capture]');
  const cameraError = root.querySelector('[data-zo-ai-camera-error]');
  const cameraErrorText = root.querySelector('[data-zo-ai-camera-error-text]');
  const cameraRetry = root.querySelector('[data-zo-ai-camera-retry]');
  const cameraUpload = root.querySelector('[data-zo-ai-camera-upload]');

  let selectedVariantId = '';
  let selectedActivity = '';
  let selectedFile = null;
  let selectedPhotoSource = 'upload';
  let previewUrl = '';
  let resultUrl = '';
  let resultBlob = null;
  let cameraStream = null;
  let busy = false;
  let sharing = false;
  let lockedScrollY = 0;
  let priorHtmlOverflow = '';
  let priorBodyStyles = null;

  function message(name, fallback) {
    return root.dataset[name] || fallback;
  }

  function setStatus(node, text, kind = '') {
    node.textContent = text || '';
    node.dataset.kind = kind;
  }

  function selectedVariantFromPage(trigger) {
    let fromUrl = '';
    try {
      fromUrl = new URLSearchParams(window.location.search).get('variant') || '';
    } catch {}
    if (/^\d+$/.test(fromUrl)) return fromUrl;

    const card = trigger?.closest?.('.card-js');
    const activeCardImage = card?.querySelector('.media > img.active[data-id]');
    const activeCardVariant = String(activeCardImage?.dataset.id || '');
    if (/^\d+$/.test(activeCardVariant)) return activeCardVariant;

    const triggerVariant = String(trigger?.dataset.variantId || '');
    if (card && /^\d+$/.test(triggerVariant)) return triggerVariant;

    const productContainer = trigger?.closest?.('.custom-main-product, [data-section-type="product"], product-info');
    const productInput = productContainer?.querySelector('form[action*="/cart/add"] [name="id"]');
    if (/^\d+$/.test(String(productInput?.value || '')) && !productInput.disabled) {
      return String(productInput.value);
    }

    if (/^\d+$/.test(triggerVariant)) return triggerVariant;

    const inputs = [...document.querySelectorAll('form[action*="/cart/add"] [name="id"]')]
      .filter((input) => /^\d+$/.test(String(input.value || '')) && !input.disabled);
    const visible = inputs.find((input) => input.offsetParent !== null);
    if (visible) return String(visible.value);
    if (inputs[0]) return String(inputs[0].value);

    return '';
  }

  function collectionActivityFromPath() {
    let pathname = '';
    try {
      pathname = decodeURIComponent(window.location.pathname).toLowerCase().replace(/\/+$/, '');
    } catch {
      pathname = String(window.location.pathname || '').toLowerCase().replace(/\/+$/, '');
    }
    const marker = pathname.indexOf('/collections/');
    if (marker < 0) return '';
    const collectionPath = `/${pathname.slice(marker + 1).split('/').slice(0, 2).join('/')}`;
    return collectionActivities[collectionPath] || '';
  }

  function setPanelLabel(id) {
    panel.setAttribute('aria-labelledby', id);
  }

  function setScene(name) {
    scenes.forEach((scene) => { scene.hidden = scene.dataset.zoAiTryonScene !== name; });
    root.dataset.scene = name;
    if (name === 'generating') setPanelLabel('zo-ai-tryon-generating-title');
    else if (name === 'result') setPanelLabel('zo-ai-tryon-result-title');
    else setPanelLabel(form.dataset.zoAiInputStep === 'photo'
      ? 'zo-ai-tryon-photo-title'
      : 'zo-ai-tryon-activity-title');
    panel.scrollTop = 0;
  }

  function focusStep(step) {
    const target = step === 'photo' ? workflowBack : activityButtons[0];
    window.setTimeout(() => target?.focus(), 340);
  }

  function setWorkflowStep(step, { focus = true } = {}) {
    const resolved = step === 'photo' ? 'photo' : 'activity';
    form.dataset.zoAiInputStep = resolved;
    workflowSteps.forEach((node) => {
      const active = node.dataset.zoAiWorkflowStep === resolved;
      node.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) node.removeAttribute('inert');
      else node.setAttribute('inert', '');
    });
    if (resolved === 'activity') {
      setPanelLabel('zo-ai-tryon-activity-title');
      stopCamera();
    } else {
      setPanelLabel('zo-ai-tryon-photo-title');
      if (selectedPhotoSource === 'camera' && !selectedFile) startCamera();
    }
    if (focus) focusStep(resolved);
  }

  function setActivity(activity, { advance = true } = {}) {
    selectedActivity = activity || '';
    activityButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.zoAiActivity === selectedActivity ? 'true' : 'false');
    });
    if (advance) setWorkflowStep('photo');
  }

  function setBusy(value) {
    busy = value;
    closeButtons.forEach((button) => { button.disabled = value; });
    activityButtons.forEach((button) => { button.disabled = value; });
    randomActivityButton.disabled = value;
    workflowBack.disabled = value;
    uploadInput.disabled = value;
    photoTabs.forEach((button) => { button.disabled = value; });
    cameraCapture.disabled = value || cameraPane.dataset.ready !== 'true' || Boolean(selectedFile);
    retakeButton.disabled = value;
    retryButton.disabled = value;
    downloadButton.disabled = value;
    shareButtons.forEach((button) => { button.disabled = value || sharing; });
    updateGenerateState();
  }

  function updateGenerateState() {
    generateButton.disabled = busy || !selectedFile || !selectedVariantId;
  }

  function stopCamera() {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      cameraStream = null;
    }
    if (cameraVideo.srcObject) cameraVideo.srcObject = null;
    cameraPane.dataset.ready = 'false';
    cameraCapture.disabled = true;
  }

  function showCameraError(text) {
    stopCamera();
    cameraError.hidden = false;
    cameraErrorText.textContent = text;
    cameraCapture.hidden = true;
  }

  async function startCamera() {
    stopCamera();
    cameraError.hidden = true;
    cameraCapture.hidden = false;
    cameraPane.dataset.ready = 'false';
    cameraCapture.disabled = true;
    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraError(message('errorCameraUnsupported', 'Camera is not supported on this device.'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 900 },
          height: { ideal: 1200 },
          aspectRatio: { ideal: 0.75 },
        },
        audio: false,
      });
      if (selectedPhotoSource !== 'camera'
        || form.dataset.zoAiInputStep !== 'photo'
        || root.hidden) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      cameraStream = stream;
      cameraVideo.srcObject = stream;
      if (cameraVideo.readyState < 1) {
        await new Promise((resolve) => cameraVideo.addEventListener('loadedmetadata', resolve, { once: true }));
      }
      await cameraVideo.play();
      cameraPane.dataset.ready = 'true';
      cameraCapture.disabled = busy || Boolean(selectedFile);
    } catch {
      showCameraError(message('errorCameraPermission', 'Camera is not available. Please allow camera access, or upload a photo instead.'));
    }
  }

  function revokePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = '';
  }

  function revokeResult() {
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    resultUrl = '';
    resultBlob = null;
    resultImage.removeAttribute('src');
  }

  function clearSelectedPhoto() {
    revokePreview();
    selectedFile = null;
    uploadInput.value = '';
    uploadLabel.textContent = defaultUploadLabel;
    preview.removeAttribute('src');
    previewWrap.hidden = true;
    generatingImage.removeAttribute('src');
    cameraCapture.hidden = false;
    setStatus(inputStatus, '');
    updateGenerateState();
  }

  function validateFile(file) {
    if (!file || !allowedTypes.has(file.type)) {
      const error = new Error('INVALID_FILE_TYPE');
      error.code = 'INVALID_FILE_TYPE';
      throw error;
    }
    if (file.size > maxBytes) {
      const error = new Error('FILE_TOO_LARGE');
      error.code = 'FILE_TOO_LARGE';
      throw error;
    }
  }

  function selectedPhoto(file, source) {
    validateFile(file);
    clearSelectedPhoto();
    selectedFile = file;
    selectedPhotoSource = source;
    previewUrl = URL.createObjectURL(file);
    preview.src = previewUrl;
    previewWrap.hidden = false;
    generatingImage.src = previewUrl;
    uploadLabel.textContent = source === 'upload' ? (file.name || defaultUploadLabel) : defaultUploadLabel;
    retakeButton.textContent = source === 'camera'
      ? message('retakeLabel', 'Retake')
      : message('replacePhotoLabel', 'Choose another photo');
    if (source === 'camera') stopCamera();
    setStatus(inputStatus, message('photoReady', 'Photo ready'), 'success');
    updateGenerateState();
  }

  function friendlyError(error) {
    if (error?.code === 'INVALID_FILE_TYPE') return message('errorType', 'Please choose a JPEG, PNG or WebP image.');
    if (error?.code === 'FILE_TOO_LARGE') return message('errorSize', 'Please choose an image under 10 MB.');
    return message('errorGeneric', 'We could not create the try-on. Please try again.');
  }

  function setPhotoMode(mode, { clear = true, start = true } = {}) {
    selectedPhotoSource = mode === 'camera' ? 'camera' : 'upload';
    if (clear) clearSelectedPhoto();
    mediaFrame.dataset.mode = selectedPhotoSource;
    photoTabs.forEach((button) => {
      const active = button.dataset.zoAiPhotoTab === selectedPhotoSource;
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    photoPanes.forEach((pane) => { pane.hidden = pane.dataset.zoAiPhotoPane !== selectedPhotoSource; });
    if (selectedPhotoSource === 'camera'
      && start
      && form.dataset.zoAiInputStep === 'photo'
      && !selectedFile) startCamera();
    else stopCamera();
  }

  function capturePhoto() {
    const videoWidth = cameraVideo.videoWidth;
    const videoHeight = cameraVideo.videoHeight;
    if (!videoWidth || !videoHeight) return;
    const targetWidth = 900;
    const targetHeight = 1200;
    const targetRatio = targetWidth / targetHeight;
    const sourceRatio = videoWidth / videoHeight;
    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = videoWidth;
    let sourceHeight = videoHeight;
    if (sourceRatio > targetRatio) {
      sourceWidth = videoHeight * targetRatio;
      sourceX = (videoWidth - sourceWidth) / 2;
    } else {
      sourceHeight = videoWidth / targetRatio;
      sourceY = (videoHeight - sourceHeight) / 2;
    }
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) return setStatus(inputStatus, friendlyError(), 'error');
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(
      cameraVideo,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    canvas.toBlob((blob) => {
      if (!blob) return setStatus(inputStatus, friendlyError(), 'error');
      try {
        selectedPhoto(new File([blob], '2nu-selfie.jpg', { type: 'image/jpeg' }), 'camera');
      } catch (error) {
        setStatus(inputStatus, friendlyError(error), 'error');
      }
    }, 'image/jpeg', 0.9);
  }

  function lockDocument() {
    lockedScrollY = window.scrollY;
    priorHtmlOverflow = document.documentElement.style.overflow;
    priorBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.width = '100%';
  }

  function unlockDocument() {
    if (!priorBodyStyles) return;
    document.documentElement.style.overflow = priorHtmlOverflow;
    document.body.style.overflow = priorBodyStyles.overflow;
    document.body.style.position = priorBodyStyles.position;
    document.body.style.top = priorBodyStyles.top;
    document.body.style.width = priorBodyStyles.width;
    window.scrollTo(0, lockedScrollY);
    priorBodyStyles = null;
  }

  function openModal(trigger, requestedVariantId = '') {
    const normalizedRequestedVariant = String(requestedVariantId || '');
    selectedVariantId = /^\d+$/.test(normalizedRequestedVariant)
      ? normalizedRequestedVariant
      : selectedVariantFromPage(trigger);
    revokeResult();
    clearSelectedPhoto();
    setActivity('', { advance: false });
    setPhotoMode('upload', { clear: false, start: false });
    setStatus(inputStatus, '');
    setStatus(resultStatus, '');
    root.hidden = false;
    lockDocument();
    const defaultActivity = collectionActivityFromPath();
    if (defaultActivity) {
      setActivity(defaultActivity, { advance: false });
      setWorkflowStep('photo');
    } else {
      setWorkflowStep('activity');
    }
    setScene('input');
    updateGenerateState();
  }

  function closeModal() {
    if (busy || sharing) return;
    stopCamera();
    clearSelectedPhoto();
    revokeResult();
    setActivity('', { advance: false });
    setPhotoMode('upload', { clear: false, start: false });
    setWorkflowStep('activity', { focus: false });
    root.hidden = true;
    unlockDocument();
    setScene('input');
  }

  async function jsonFetch(url, options = {}) {
    const response = await fetch(url, {
      cache: 'no-store',
      ...options,
      headers: {
        ...(options.body ? { 'content-type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(payload.error || `HTTP_${response.status}`);
      error.code = payload.error || `HTTP_${response.status}`;
      throw error;
    }
    return payload;
  }

  async function pollResult(requestId, requestToken) {
    const deadline = Date.now() + 10 * 60 * 1000;
    while (Date.now() < deadline) {
      const payload = await jsonFetch(`${apiBase}/result`, {
        method: 'POST',
        body: JSON.stringify({ requestId, requestToken }),
      });
      if (payload.status === 'done') return;
      if (payload.status === 'failed') {
        const error = new Error(payload.error || 'GENERATION_FAILED');
        error.code = payload.error || 'GENERATION_FAILED';
        throw error;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
    }
    const error = new Error('GENERATION_TIMEOUT');
    error.code = 'GENERATION_TIMEOUT';
    throw error;
  }

  async function fetchResultBlob(requestId, requestToken) {
    const response = await fetch(`${apiBase}/result-file`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ requestId, requestToken }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const error = new Error(payload.error || `HTTP_${response.status}`);
      error.code = payload.error || `HTTP_${response.status}`;
      throw error;
    }
    return response.blob();
  }

  async function generate(event) {
    event.preventDefault();
    if (!selectedFile || !selectedVariantId || busy) return;
    try {
      validateFile(selectedFile);
    } catch (error) {
      return setStatus(inputStatus, friendlyError(error), 'error');
    }

    setBusy(true);
    setStatus(inputStatus, message('uploading', 'Uploading your photo…'));
    try {
      const initialized = await jsonFetch(`${apiBase}/init`, {
        method: 'POST',
        body: JSON.stringify({ inputFileType: selectedFile.type }),
      });
      const upload = await fetch(initialized.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': selectedFile.type },
        body: selectedFile,
      });
      if (!upload.ok) {
        const error = new Error('UPLOAD_FAILED');
        error.code = upload.status === 413 ? 'FILE_TOO_LARGE' : 'UPLOAD_FAILED';
        throw error;
      }
      setScene('generating');
      await jsonFetch(`${apiBase}/generate`, {
        method: 'POST',
        body: JSON.stringify({
          requestId: initialized.requestId,
          requestToken: initialized.requestToken,
          sport: selectedActivity,
          productVariantId: selectedVariantId,
          marketCountryCode,
        }),
      });
      await pollResult(initialized.requestId, initialized.requestToken);
      const blob = await fetchResultBlob(initialized.requestId, initialized.requestToken);
      revokeResult();
      resultBlob = blob;
      resultUrl = URL.createObjectURL(blob);
      resultImage.src = resultUrl;
      setStatus(resultStatus, '');
      setScene('result');
    } catch (error) {
      setScene('input');
      setWorkflowStep('photo', { focus: false });
      setStatus(inputStatus, friendlyError(error), 'error');
    } finally {
      setBusy(false);
    }
  }

  function resultFile() {
    if (!resultBlob) return null;
    const mime = resultBlob.type || 'image/jpeg';
    const extension = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : 'jpg';
    return new File([resultBlob], `2nu-ai-try-on.${extension}`, { type: mime });
  }

  function downloadResult() {
    const file = resultFile();
    if (!file) return false;
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.hidden = true;
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  }

  async function shareResult(platform) {
    if (sharing || !resultBlob) return;
    const file = resultFile();
    if (!file) return;
    setStatus(resultStatus, message('sharePreparing', 'Preparing your image…'));
    const sharePayload = {
      files: [file],
      title: message('shareTitle', 'My 2nu AI Try-On'),
      text: message('shareText', 'See my 2nu AI Try-On.'),
    };

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      sharing = true;
      setBusy(busy);
      try {
        await navigator.share(sharePayload);
        setStatus(resultStatus, '');
      } catch (error) {
        if (error?.name !== 'AbortError') {
          setStatus(resultStatus, message('shareError', 'We could not open sharing. Please download the image and share it from your device.'), 'error');
        }
      } finally {
        sharing = false;
        setBusy(busy);
      }
      return;
    }

    if (downloadResult()) {
      const fallback = platform === 'instagram'
        ? message('shareInstagramFallback', 'The image has been downloaded. Open Instagram to share it.')
        : message('shareDownloadFallback', 'Sharing is not available on this device. The image has been downloaded instead.');
      setStatus(resultStatus, fallback, 'success');
    } else {
      setStatus(resultStatus, message('shareError', 'We could not open sharing. Please download the image and share it from your device.'), 'error');
    }
  }

  function tryAnotherPhoto() {
    if (busy || sharing) return;
    revokeResult();
    clearSelectedPhoto();
    setStatus(inputStatus, '');
    setStatus(resultStatus, '');
    setPhotoMode('upload', { clear: false, start: false });
    setWorkflowStep('photo', { focus: false });
    setScene('input');
    updateGenerateState();
    window.setTimeout(() => uploadInput.focus(), 0);
  }

  function focusableControls() {
    return [...panel.querySelectorAll('button, input, [href], [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.disabled
        && !node.hidden
        && !node.closest('[hidden]')
        && !node.closest('[inert]')
        && node.getClientRects().length > 0);
  }

  document.addEventListener('two-nu:ai-tryon-request', (event) => {
    const requestedVariantId = String(event.detail?.variantId || '');
    if (!/^\d+$/.test(requestedVariantId) || !root.hidden) return;
    event.preventDefault();
    openModal(null, requestedVariantId);
  }, true);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('#ai-tryon-btn, [data-ai-tryon-trigger]');
    if (!trigger || !root.hidden) return;
    event.preventDefault();
    openModal(trigger);
  }, true);

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[name="id"], input[type="radio"], select')) return;
    window.setTimeout(() => {
      const current = selectedVariantFromPage(null);
      if (current) selectedVariantId = current;
      updateGenerateState();
    }, 0);
  });

  window.addEventListener('popstate', () => {
    const current = selectedVariantFromPage(null);
    if (current) selectedVariantId = current;
    updateGenerateState();
  });

  activityButtons.forEach((button) => button.addEventListener('click', () => {
    if (!busy) setActivity(button.dataset.zoAiActivity);
  }));
  randomActivityButton.addEventListener('click', () => {
    if (!busy) setActivity('');
  });
  workflowBack.addEventListener('click', () => {
    if (!busy) setWorkflowStep('activity');
  });
  uploadInput.addEventListener('change', () => {
    const file = uploadInput.files?.[0];
    if (!file) return;
    try {
      selectedPhoto(file, 'upload');
    } catch (error) {
      clearSelectedPhoto();
      setStatus(inputStatus, friendlyError(error), 'error');
    }
  });
  photoTabs.forEach((button) => button.addEventListener('click', () => {
    if (!busy) setPhotoMode(button.dataset.zoAiPhotoTab);
  }));
  photoTabs.forEach((button, index) => button.addEventListener('keydown', (event) => {
    if (busy || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = photoTabs.length - 1;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + photoTabs.length) % photoTabs.length;
    else nextIndex = (index + 1) % photoTabs.length;
    const nextTab = photoTabs[nextIndex];
    setPhotoMode(nextTab.dataset.zoAiPhotoTab);
    nextTab.focus();
  }));
  cameraCapture.addEventListener('click', capturePhoto);
  cameraRetry.addEventListener('click', startCamera);
  cameraUpload.addEventListener('click', () => setPhotoMode('upload'));
  retakeButton.addEventListener('click', () => {
    const source = selectedPhotoSource;
    clearSelectedPhoto();
    if (source === 'camera') startCamera();
    else uploadInput.click();
  });
  form.addEventListener('submit', generate);
  retryButton.addEventListener('click', tryAnotherPhoto);
  downloadButton.addEventListener('click', () => {
    if (!downloadResult()) {
      setStatus(resultStatus, message('shareError', 'We could not download the image. Please try again.'), 'error');
    }
  });
  shareButtons.forEach((button) => button.addEventListener('click', () => shareResult(button.dataset.zoAiShare)));
  closeButtons.forEach((button) => button.addEventListener('click', closeModal));
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = focusableControls();
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
  window.addEventListener('pagehide', stopCamera);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopCamera();
    } else if (!root.hidden
      && form.dataset.zoAiInputStep === 'photo'
      && selectedPhotoSource === 'camera'
      && !selectedFile) {
      startCamera();
    }
  });

  setActivity('', { advance: false });
  setPhotoMode('upload', { clear: false, start: false });
  setWorkflowStep('activity', { focus: false });
  setScene('input');
  setBusy(false);
})();
